/**
 * A* pathfinding on the navigation graph (binary min-heap open set).
 */

/** @typedef {import('./NavigationGraph.js').NavigationGraph} NavigationGraph */

/**
 * @param {NavigationGraph} graph
 * @param {number} startId
 * @param {number} goalId
 * @returns {number[]} node id path (includes start and goal)
 */
export function findPath(graph, startId, goalId) {
  if (startId === goalId) return [startId];
  if (!graph.getNode(startId) || !graph.getNode(goalId)) return [];

  const goal = graph.getNode(goalId);
  /** @type {Map<number, number>} */
  const gScore = new Map([[startId, 0]]);
  /** @type {Map<number, number>} */
  const cameFrom = new Map();
  /** @type {Map<number, number>} */
  const fScore = new Map([
    [startId, heuristic(graph, startId, goal)],
  ]);

  const open = new MinHeap();
  open.push(startId, fScore.get(startId));

  while (open.length > 0) {
    const current = open.pop();
    if (current === undefined) break;
    if (current === goalId) {
      return reconstruct(cameFrom, current);
    }

    const edges = graph.edges.get(current) ?? [];
    const gCurrent = gScore.get(current) ?? Infinity;
    for (const edge of edges) {
      const tentative = gCurrent + edge.cost;
      if (tentative >= (gScore.get(edge.to) ?? Infinity)) continue;
      cameFrom.set(edge.to, current);
      gScore.set(edge.to, tentative);
      const f = tentative + heuristic(graph, edge.to, goal);
      fScore.set(edge.to, f);
      open.pushOrDecrease(edge.to, f);
    }
  }

  return [];
}

function heuristic(graph, nodeId, goal) {
  const node = graph.getNode(nodeId);
  return Math.hypot(goal.x - node.x, goal.z - node.z);
}

function reconstruct(cameFrom, current) {
  /** @type {number[]} */
  const path = [current];
  while (cameFrom.has(current)) {
    current = cameFrom.get(current);
    path.unshift(current);
  }
  return path;
}

class MinHeap {
  constructor() {
    /** @type {number[]} */
    this._ids = [];
    /** @type {number[]} */
    this._prio = [];
    /** @type {Map<number, number>} */
    this._index = new Map();
  }

  get length() {
    return this._ids.length;
  }

  /** @param {number} id @param {number} priority */
  push(id, priority) {
    this._ids.push(id);
    this._prio.push(priority);
    this._index.set(id, this._ids.length - 1);
    this._bubbleUp(this._ids.length - 1);
  }

  /** @param {number} id @param {number} priority */
  pushOrDecrease(id, priority) {
    const idx = this._index.get(id);
    if (idx === undefined) {
      this.push(id, priority);
      return;
    }
    if (priority >= this._prio[idx]) return;
    this._prio[idx] = priority;
    this._bubbleUp(idx);
  }

  pop() {
    if (this._ids.length === 0) return undefined;
    const id = this._ids[0];
    this._index.delete(id);
    const lastId = this._ids.pop();
    const lastP = this._prio.pop();
    if (this._ids.length > 0 && lastId !== undefined) {
      this._ids[0] = lastId;
      this._prio[0] = lastP;
      this._index.set(lastId, 0);
      this._sinkDown(0);
    }
    return id;
  }

  _bubbleUp(i) {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this._prio[p] <= this._prio[i]) break;
      this._swap(p, i);
      i = p;
    }
  }

  _sinkDown(i) {
    const n = this._ids.length;
    while (true) {
      const l = i * 2 + 1;
      const r = l + 1;
      let smallest = i;
      if (l < n && this._prio[l] < this._prio[smallest]) smallest = l;
      if (r < n && this._prio[r] < this._prio[smallest]) smallest = r;
      if (smallest === i) break;
      this._swap(i, smallest);
      i = smallest;
    }
  }

  _swap(a, b) {
    const idA = this._ids[a];
    const idB = this._ids[b];
    this._ids[a] = idB;
    this._ids[b] = idA;
    const pA = this._prio[a];
    this._prio[a] = this._prio[b];
    this._prio[b] = pA;
    this._index.set(idA, b);
    this._index.set(idB, a);
  }
}
